import { ConfirmActionModal } from '@/shared/ui';
import { toaster } from '@/shared/ui/toaster.component';
import {
  Button,
  Dialog,
  RadioGroup,
} from '@noorden-insurtech/alba-design-system';
import { AlertTriangle, Layers } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useBulkBrokerProducts, useProductsCatalog } from '../hooks';
import type { BulkCandidateFilters } from '../hooks/use-bulk-broker-candidates.hook';
import {
  BULK_MAX_PRODUCTS,
  BULK_MAX_SELECTION,
  buildRetryJobs,
  estimateRunSeconds,
  formatEstimate,
  type BulkBrokerRef,
  type BulkProductAction,
  type BulkProductJob,
} from '../utils/bulk-broker-products.util';
import { BulkProductsBrokerPicker } from './bulk-products-broker-picker.component';
import { BulkProductsProductPicker } from './bulk-products-product-picker.component';
import { BulkProductsProgress } from './bulk-products-progress.component';
import { BulkProductsResult } from './bulk-products-result.component';

interface BulkProductsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Filtros da tela, para o admin não recomeçar do zero. */
  initialFilters: BulkCandidateFilters;
}

type Step = 'form' | 'review';

export function BulkProductsModal({
  isOpen,
  onClose,
  initialFilters,
}: BulkProductsModalProps) {
  const [step, setStep] = useState<Step>('form');
  const [brokers, setBrokers] = useState<Map<string, BulkBrokerRef>>(new Map());
  const [products, setProducts] = useState<Set<string>>(new Set());
  const [action, setAction] = useState<BulkProductAction>('ACTIVATE');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { state, run, cancel, reset } = useBulkBrokerProducts();

  // Mesma queryKey do seletor de produtos: vem do cache, sem request extra.
  const { data: catalog } = useProductsCatalog();

  /** "Seguro Residencial (140001)" — só o código não diz nada na revisão. */
  const describeProduct = (code: string) => {
    const product = catalog?.find(item => item.code === code);
    return product ? `${product.name} (${product.code})` : code;
  };

  const isRunning = state.status === 'running' || state.status === 'cancelling';
  const isDone = state.status === 'done';

  const canApply =
    brokers.size > 0 &&
    brokers.size <= BULK_MAX_SELECTION &&
    products.size > 0 &&
    products.size <= BULK_MAX_PRODUCTS;

  const estimate = useMemo(
    () => formatEstimate(estimateRunSeconds(brokers.size, products.size)),
    [brokers.size, products.size]
  );

  const closeAndReset = () => {
    if (isRunning) return;

    setStep('form');
    setBrokers(new Map());
    setProducts(new Set());
    setAction('ACTIVATE');
    setConfirmOpen(false);
    reset();
    onClose();
  };

  const startRun = async (jobs: BulkProductJob[]) => {
    setConfirmOpen(false);
    await run(jobs, action);

    toaster.create({
      type: 'success',
      title: 'Operação concluída',
      description: 'Confira o resumo na janela.',
    });
  };

  const applySelection = () =>
    startRun(
      [...brokers.values()].map(broker => ({
        broker,
        productCodes: [...products],
      }))
    );

  const retryFailed = () => startRun(buildRetryJobs(state.failures));

  const handleApplyClick = () => {
    // Desativar é o caminho que tira produto de dezenas de corretoras: exige a
    // barreira de digitação. Ativar é reversível e idempotente.
    if (action === 'DEACTIVATE') {
      setConfirmOpen(true);
      return;
    }

    void applySelection();
  };

  return (
    <>
      <Dialog.Root
        open={isOpen}
        // Enquanto roda, fechar mataria o estado (progresso e lista de falhas)
        // sem interromper as requisições já disparadas.
        onOpenChange={details => {
          if (!details.open) closeAndReset();
        }}
        closeOnInteractOutside={!isRunning}
        closeOnEscape={!isRunning}
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content className='flex max-h-[92vh] w-[1080px] max-w-[95vw] flex-col rounded-3xl bg-[#F9F9F9] p-0 shadow-xl'>
            <Dialog.Header className='px-6 pb-3 pt-7 sm:pb-0 sm:pt-6'>
              <Dialog.Title className='w-full'>
                <div className='flex items-center gap-3 text-alba-black-500'>
                  <Layers className='h-6 w-6' strokeWidth={1.8} />
                  <span className='text-xl font-bold leading-6'>
                    Gerenciar produtos em lote
                  </span>
                </div>
              </Dialog.Title>
            </Dialog.Header>

            <Dialog.Body className='max-h-[70vh] overflow-y-auto px-6 pb-4 pt-4'>
              {isRunning && (
                <BulkProductsProgress state={state} onCancel={cancel} />
              )}

              {isDone && (
                <BulkProductsResult state={state} onRetryFailed={retryFailed} />
              )}

              {!isRunning && !isDone && step === 'form' && (
                <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                  <BulkProductsBrokerPicker
                    initialFilters={initialFilters}
                    selected={brokers}
                    onChange={setBrokers}
                  />

                  <div className='flex flex-col gap-6'>
                    <BulkProductsProductPicker
                      selected={products}
                      onChange={setProducts}
                    />

                    <section className='flex flex-col gap-3'>
                      <h3 className='text-sm font-bold uppercase tracking-[0.04em] text-alba-gray-600'>
                        3. O que fazer
                      </h3>

                      <RadioGroup.Root
                        value={action}
                        onValueChange={details =>
                          setAction(details.value as BulkProductAction)
                        }
                      >
                        {/* O ponto preenchido e o `Indicator`, um span proprio
                            que so ganha opacidade com group-data-[state=checked]
                            do Item. Sem ele dentro do Control, o circulo fica
                            sempre vazio. */}
                        <RadioGroup.Item value='ACTIVATE'>
                          <RadioGroup.ItemControl>
                            <RadioGroup.Indicator />
                          </RadioGroup.ItemControl>
                          <RadioGroup.ItemText>Ativar</RadioGroup.ItemText>
                          <RadioGroup.ItemHiddenInput />
                        </RadioGroup.Item>
                        <RadioGroup.Item value='DEACTIVATE'>
                          <RadioGroup.ItemControl>
                            <RadioGroup.Indicator />
                          </RadioGroup.ItemControl>
                          <RadioGroup.ItemText>Desativar</RadioGroup.ItemText>
                          <RadioGroup.ItemHiddenInput />
                        </RadioGroup.Item>
                      </RadioGroup.Root>

                      <p className='text-sm text-alba-gray-600'>
                        Produtos não marcados ficam como estão em cada
                        corretora. Ativar um produto que já está ativo não muda
                        nada.
                      </p>

                      {action === 'DEACTIVATE' && (
                        <p className='flex items-start gap-2 rounded-lg border border-alba-warning-orange-400 bg-alba-warning-orange-400/10 px-3 py-2 text-sm text-alba-gray-700'>
                          <AlertTriangle
                            className='mt-0.5 h-4 w-4 shrink-0 text-alba-warning-orange-500'
                            strokeWidth={1.8}
                          />
                          Desativar remove o produto das corretoras
                          selecionadas. Quem não tiver o produto não é afetado.
                        </p>
                      )}
                    </section>
                  </div>
                </div>
              )}

              {!isRunning && !isDone && step === 'review' && (
                <div className='flex flex-col gap-3 py-4'>
                  <p className='text-base text-alba-black-500'>
                    Você vai{' '}
                    <strong>
                      {action === 'ACTIVATE' ? 'ATIVAR' : 'DESATIVAR'}
                    </strong>{' '}
                    {products.size} produto(s) em {brokers.size} corretora(s).
                  </p>
                  <p className='text-sm text-alba-gray-700'>
                    <strong>Produtos:</strong>{' '}
                    {[...products].map(describeProduct).join(', ')}
                  </p>
                  <p className='text-sm text-alba-gray-700'>
                    <strong>Corretoras:</strong>{' '}
                    {[...brokers.values()]
                      .slice(0, 4)
                      .map(b => b.name)
                      .join(', ')}
                    {brokers.size > 4 && ` … e mais ${brokers.size - 4}`}
                  </p>
                  <p className='text-sm text-alba-gray-600'>
                    A operação leva {estimate || 'alguns segundos'} e não pode
                    ser desfeita em massa.
                  </p>
                </div>
              )}
            </Dialog.Body>

            {/* No mobile o rodape empilha: lado a lado, o resumo espremia os
                botoes e o rotulo do primario ficava cortado. */}
            <Dialog.Footer className='flex flex-col gap-3 px-6 pb-6 pt-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4'>
              <span className='text-sm text-alba-gray-600'>
                {!isRunning && !isDone && canApply && (
                  <>
                    {brokers.size} corretora(s) · {products.size} produto(s) ·{' '}
                    {action === 'ACTIVATE' ? 'ATIVAR' : 'DESATIVAR'}
                    {estimate && ` · ${estimate}`}
                  </>
                )}
              </span>

              <div className='flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:gap-4'>
                {isDone ? (
                  <Button variant='primary' onClick={closeAndReset}>
                    Fechar
                  </Button>
                ) : isRunning ? null : step === 'form' ? (
                  <>
                    <Button variant='ghost' onClick={closeAndReset}>
                      Cancelar
                    </Button>
                    <Button
                      variant='primary'
                      disabled={!canApply}
                      onClick={() => setStep('review')}
                    >
                      Revisar e aplicar ({brokers.size})
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant='ghost' onClick={() => setStep('form')}>
                      Voltar
                    </Button>
                    <Button variant='primary' onClick={handleApplyClick}>
                      {action === 'ACTIVATE' ? 'Ativar' : 'Desativar'} (
                      {brokers.size})
                    </Button>
                  </>
                )}
              </div>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>

      <ConfirmActionModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => void applySelection()}
        tone='danger'
        confirmationWord='desativar'
        confirmLabel='Desativar'
        title='Desativar produtos em lote'
        description={`${products.size} produto(s) serão removidos de ${brokers.size} corretora(s).`}
        warning='As corretoras deixam de poder cotar e emitir esses produtos imediatamente.'
      />
    </>
  );
}
